// src/components/KYC.jsx
import React, { useEffect, useState } from "react";
import "./KYC.css";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";

export default function KYC() {
  const navigate = useNavigate();

  const [userId, setUserId] = useState(null);
  const [status, setStatus] = useState("unverified");
  const [country, setCountry] = useState("");
  const [docType, setDocType] = useState("");
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);

  const [tempFile, setTempFile] = useState(null);
  const [tempPreview, setTempPreview] = useState(null);
  const [tempTarget, setTempTarget] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [loadingNext, setLoadingNext] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [step, setStep] = useState(1);

  const [notif, setNotif] = useState(null);

  useEffect(() => {
    (async () => {
      const { data: userResp } = await supabase.auth.getUser();
      if (!userResp?.user) return;
      setUserId(userResp.user.id);

      const { data } = await supabase
        .from("users_data")
        .select("kyc_status, kyc_country, kyc_document_type, kyc_front_url, kyc_back_url")
        .eq("user_id", userResp.user.id)
        .single();

      if (data?.kyc_status) setStatus(data.kyc_status);
      if (data?.kyc_country) setCountry(data.kyc_country);
      if (data?.kyc_document_type) setDocType(data.kyc_document_type);
      if (data?.kyc_front_url) setFrontPreview(data.kyc_front_url);
      if (data?.kyc_back_url) setBackPreview(data.kyc_back_url);
      if (data?.kyc_status === "pending") setStep(2);
    })();
  }, []);

  const showNotif = (type, title, message, timeout = 3500) => {
    setNotif({ type, title, message });
    if (timeout) setTimeout(() => setNotif(null), timeout);
  };

  const handleFilePicked = (file, target) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setTempFile(file);
    setTempPreview(url);
    setTempTarget(target);
    setShowConfirmModal(true);
  };

  const confirmAttach = async () => {
    setLoadingConfirm(true);
    await new Promise((r) => setTimeout(r, 350));
    if (tempTarget === "front") {
      setFrontFile(tempFile);
      setFrontPreview(tempPreview);
    } else {
      setBackFile(tempFile);
      setBackPreview(tempPreview);
    }
    setTempFile(null);
    setTempPreview(null);
    setTempTarget(null);
    setShowConfirmModal(false);
    setLoadingConfirm(false);
  };

  const removeAttached = (which) => {
    if (which === "front") {
      setFrontFile(null);
      setFrontPreview(null);
    } else {
      setBackFile(null);
      setBackPreview(null);
    }
  };

  const goNext = async () => {
    if (!country) return;
    setLoadingNext(true);
    await new Promise((r) => setTimeout(r, 350));
    setStep(2);
    setLoadingNext(false);
  };

  const uploadToBucket = async (file, remotePath) => {
    if (!file) throw new Error("No file");
    const cleanName = (file.name || "file").replace(/[^a-zA-Z0-9.\-_]/g, "_");
    const path = `${remotePath.replace(/\s+/g, "_")}-${cleanName}`;

    const { error } = await supabase.storage.from("kyc_docs").upload(path, file, { upsert: true });
    if (error) throw new Error("Upload failed");

    const { data: urlData } = supabase.storage.from("kyc_docs").getPublicUrl(path);
    return urlData.publicUrl;
  };

  const submitKYC = async () => {
    if (!userId) return;
    if (!country || !docType) return;
    if (!frontFile || !backFile) return;

    setLoadingSubmit(true);

    try {
      const ts = Date.now();
      const frontPath = `kyc/${userId}/front-${ts}`;
      const backPath = `kyc/${userId}/back-${ts}`;

      const frontUrl = await uploadToBucket(frontFile, frontPath);
      const backUrl = await uploadToBucket(backFile, backPath);

      await supabase
        .from("users_data")
        .update({
          kyc_status: "pending",
          kyc_country: country,
          kyc_document_type: docType,
          kyc_front_url: frontUrl,
          kyc_back_url: backUrl,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", userId);

      setStatus("pending");
    } catch (err) {
    } finally {
      setLoadingSubmit(false);
    }
  };

  if (status === "verified") {
    return (
      <div className="kyc-page">
        <h2>Identity Verification</h2>
        <div className="kyc-card small">
          <h3 className="kyc-verified">Verified ✔</h3>
          <button className="kyc-back-btn" onClick={() => navigate("/profile")}>Back</button>
        </div>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="kyc-page">
        <h2>Identity Verification</h2>
        <div className="kyc-card small">
          <h3 className="kyc-pending">Verification Pending…</h3>
          <button className="kyc-back-btn" onClick={() => navigate("/profile")}>Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="kyc-page">
      <h2>Identity Verification</h2>

      {step === 1 && (
        <div className="kyc-card">
          <label>Country</label>
          <select value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">Choose country</option>
            <option>Pakistan</option>
            <option>India</option>
            <option>USA</option>
            <option>UK</option>
            <option>UAE</option>
            <option>Turkey</option>
            <option>Saudi Arabia</option>
          </select>

          <label style={{ marginTop: 12 }}>Document Type</label>
          <select value={docType} onChange={(e) => setDocType(e.target.value)}>
            <option value="">Choose document</option>
            <option>National ID</option>
            <option>Passport</option>
            <option>Driving License</option>
          </select>

          <div className="kyc-actions">
            <button className="kyc-btn" onClick={goNext} disabled={loadingNext}>
              {loadingNext ? <span className="small-spinner" /> : "Next"}
            </button>
            <button className="kyc-back-btn" onClick={() => navigate("/profile")}>Cancel</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="kyc-card">
          <div className="upload-row">
            <div className="upload-box">
              {frontPreview ? (
                <>
                  <img src={frontPreview} alt="front" className="upload-thumb" />
                  <button className="remove-btn" onClick={() => removeAttached("front")}>Remove</button>
                </>
              ) : (
                <label className="upload-placeholder">
                  <input type="file" accept="image/*" onChange={(e) => handleFilePicked(e.target.files?.[0], "front")} />
                  <div className="plus">+</div>
                  <div className="hint">Front of document</div>
                </label>
              )}
            </div>

            <div className="upload-box">
              {backPreview ? (
                <>
                  <img src={backPreview} alt="back" className="upload-thumb" />
                  <button className="remove-btn" onClick={() => removeAttached("back")}>Remove</button>
                </>
              ) : (
                <label className="upload-placeholder">
                  <input type="file" accept="image/*" onChange={(e) => handleFilePicked(e.target.files?.[0], "back")} />
                  <div className="plus">+</div>
                  <div className="hint">Back of document</div>
                </label>
              )}
            </div>
          </div>

          <div className="kyc-actions">
            <button className="kyc-btn primary" onClick={submitKYC} disabled={loadingSubmit}>
              {loadingSubmit ? <span className="small-spinner" /> : "Submit for Verification"}
            </button>
            <button className="kyc-back-btn" onClick={() => setStep(1)}>Back</button>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3>Confirm file</h3>
            <div className="modal-preview">
              <img src={tempPreview} alt="preview" />
            </div>
            <div className="modal-actions">
              <button className="btn ghost" onClick={() => { setShowConfirmModal(false); setTempFile(null); setTempPreview(null); setTempTarget(null); }}>
                Cancel
              </button>
              <button className="btn primary" onClick={confirmAttach} disabled={loadingConfirm}>
                {loadingConfirm ? <span className="small-spinner" /> : "Attach file"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FloatingNotif({ type, title, message }) {
  return (
    <div className={`kyc-floating-notif ${type || "info"}`}>
      <div className="fn-head">
        <strong>{title}</strong>
        <span className="fn-close">&times;</span>
      </div>
      <div className="fn-body">{message}</div>
    </div>
  );
}
